import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Inbox, Plus, Clock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { listTickets, subscribe } from "../data/demoStore";

const statusConfig = {
  open: { label: "In Progress", icon: Loader2, className: "bg-blue-100 text-blue-700" },
  pending_approval: { label: "Awaiting Approval", icon: Clock, className: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolved", icon: CheckCircle, className: "bg-green-100 text-green-700" },
  closed: { label: "Closed", icon: CheckCircle, className: "bg-slate-100 text-slate-600" },
};

export function MyRequests() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(listTickets());

  useEffect(() => subscribe(() => setTickets(listTickets())), []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Inbox className="w-5 h-5 text-muted-foreground" />
            My Requests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Track the status of your IT requests.</p>
        </div>
        <Button asChild size="sm">
          <Link to="/portal">
            <Plus className="w-4 h-4 mr-2" />
            New request
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Your Requests</CardTitle>
          <CardDescription>Most recent first</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tickets.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">You haven't submitted any requests yet.</p>
              <Button asChild size="sm">
                <Link to="/portal">Submit your first request</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {tickets.map(t => {
                const status = statusConfig[t.status] || statusConfig.open;
                const StatusIcon = status.icon;
                return (
                  <div
                    key={t.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tickets/${t.id}`)}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${status.className}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-foreground truncate">{t.subject}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{t.id}</span>
                          <span>·</span>
                          <span>{formatDate(t.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="outline" className={`text-xs ${status.className} border-0`}>
                        {status.label}
                      </Badge>
                      <Button size="sm" variant="ghost">
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MyRequests;
